import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import AuthContext from '../context/AuthContext';
import NotificationContext from '../context/NotificationContext';
import { Box, CircularProgress, Container, Typography, Button, Chip, Switch, FormControlLabel } from '@mui/material';
import StudentsComponent from '../components/StudentsComponent';
import AddIcon from '@mui/icons-material/Add';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';

const StudentsPage = () => {
    const { authTokens, logoutUser, backendUrl, user, pageTitle } = useContext(AuthContext);
    const { showToast } = useContext(NotificationContext);
    const [studentsRaw, setStudentsRaw] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [absentTodayMap, setAbsentTodayMap] = useState({});
    const [isLoading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [filterNeedsRoute, setFilterNeedsRoute] = useState(false);
    const [filterHasRoute, setFilterHasRoute] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens.access)
    };

    document.title = pageTitle + ' - ' + t('students.title');

    const onStudentDelete = () => {
        getStudents();
    };

    const getStudents = useCallback(async () => {
        try {
            const response = await fetch(backendUrl + 'api/students/', {
                method: 'GET',
                headers,
            });
            if (response.status === 401) { logoutUser(); return; }
            const data = await response.json();
            if (Array.isArray(data)) setStudentsRaw(data);
        } catch { showToast(t('common.network_error'), 'error'); }
    }, []);

    const fetchRoutes = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/get-routes/', { headers });
            if (res.status === 401) { logoutUser(); return; }
            const data = await res.json();
            if (Array.isArray(data)) setRoutes(data);
        } catch (e) { console.error(e); }
    }, []);

    const fetchAbsentToday = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/absences-for-date/', { headers });
            if (res.ok) {
                const items = await res.json();
                const map = {};
                items.forEach(a => { map[a.child_id] = a; });
                setAbsentTodayMap(map);
            }
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        Promise.all([getStudents(), fetchRoutes(), fetchAbsentToday()]).then(() => setLoading(false));
    }, []);

    const routeToMap = useMemo(() => {
        const map = {};
        routes.forEach(route => {
            if (route.route_type === 0) {
                (route.stops || []).forEach(stop => { map[stop.child_id] = route.name; });
            }
        });
        return map;
    }, [routes]);

    const routeFromMap = useMemo(() => {
        const map = {};
        routes.forEach(route => {
            if (route.route_type === 1) {
                (route.stops || []).forEach(stop => { map[stop.child_id] = route.name; });
            }
        });
        return map;
    }, [routes]);

    const students = useMemo(() => {
        return studentsRaw.map(student => ({
            id: student.child_id,
            child_id: student.child_id,
            firstname: student.firstname,
            lastname: student.lastname,
            schoolclass_name: student.schoolclass_name,
            department_name: student.department_name,
            bus_to_school: student.bus_to_school,
            bus_from_school: student.bus_from_school,
            is_active: student.is_active,
            parents: student.parents || [],
            route_to: student.bus_to_school ? (routeToMap[student.child_id] || null) : undefined,
            route_from: student.bus_from_school ? (routeFromMap[student.child_id] || null) : undefined,
            is_absent_today: absentTodayMap[student.child_id]?.absence_type === 0,
            is_bus_absent_to: absentTodayMap[student.child_id]?.absence_type === 1 && absentTodayMap[student.child_id]?.skip_to_school,
            is_bus_absent_from: absentTodayMap[student.child_id]?.absence_type === 1 && absentTodayMap[student.child_id]?.skip_from_school,
        }));
    }, [studentsRaw, routeToMap, routeFromMap, absentTodayMap]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress
                        size={44}
                        thickness={3}
                        sx={{ color: AMBER }}
                    />
                    <Typography
                        sx={{
                            mt: 2,
                            fontFamily: '"Outfit", sans-serif',
                            fontSize: '0.875rem',
                            color: '#64748b',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {t('students.loading')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
            <Container maxWidth="xl">

                {/* ── Page Header ── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 42,
                                height: 42,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`,
                                boxShadow: `0 4px 14px rgba(15,45,94,0.25)`,
                                flexShrink: 0,
                            }}
                        >
                            <PeopleAltIcon sx={{ color: AMBER, fontSize: 22 }} />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: '"Outfit", sans-serif',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    color: NAV_BG,
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {t('students.title')}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Outfit", sans-serif',
                                    fontSize: '0.8rem',
                                    color: '#94a3b8',
                                    fontWeight: 400,
                                    mt: 0.1,
                                }}
                            >
                                {t('students.subtitle')}
                            </Typography>
                        </Box>

                        <Chip
                            label={showInactive ? students.length : students.filter(s => s.is_active).length}
                            size="small"
                            sx={{
                                ml: 0.5,
                                fontFamily: '"Outfit", sans-serif',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                                color: NAV_BG,
                                boxShadow: `0 2px 8px ${AMBER_GLOW}`,
                                border: 'none',
                                height: 24,
                                alignSelf: 'flex-start',
                                mt: 0.25,
                            }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/add-student')}
                        sx={{
                            fontFamily: '"Outfit", sans-serif',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 2.5,
                            py: 1,
                            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                            color: NAV_BG,
                            boxShadow: `0 4px 14px ${AMBER_GLOW}`,
                            border: 'none',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`,
                                boxShadow: `0 6px 20px rgba(245,158,11,0.35)`,
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        {t('students.add')}
                    </Button>
                </Box>

                {/* ── Filters ── */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                    <FormControlLabel
                        control={<Switch size="small" checked={filterHasRoute} onChange={(e) => { setFilterHasRoute(e.target.checked); if (e.target.checked) setFilterNeedsRoute(false); }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#16a34a' } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#15803d' }}>{t('students.filter_has_route')}</Typography>}
                    />
                    <FormControlLabel
                        control={<Switch size="small" checked={filterNeedsRoute} onChange={(e) => { setFilterNeedsRoute(e.target.checked); if (e.target.checked) setFilterHasRoute(false); }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f97316' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f97316' } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#c2410c' }}>{t('students.filter_needs_route')}</Typography>}
                    />
                    <FormControlLabel
                        control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#92400e' }}>{t('students.show_inactive')}</Typography>}
                    />
                </Box>

                {/* ── Students Table ── */}
                <StudentsComponent
                    onStudentDelete={onStudentDelete}
                    students={(() => {
                        let filtered = showInactive ? students : students.filter(s => s.is_active);
                        if (filterNeedsRoute) {
                            filtered = filtered.filter(s =>
                                (s.bus_to_school && s.route_to === null) ||
                                (s.bus_from_school && s.route_from === null)
                            );
                        }
                        if (filterHasRoute) {
                            filtered = filtered.filter(s =>
                                (s.bus_to_school && s.route_to) ||
                                (s.bus_from_school && s.route_from)
                            );
                        }
                        return filtered;
                    })()}
                />

            </Container>
        </Box>
    );
};

export default StudentsPage;
